import React from 'react';
import { useModal } from 'store/modals';
import { LOCAL_STORAGE_KEYS, useLocalStorage } from 'utils/local-storage';
import { LocationPermissionDialog } from './LocationPermissionDialog';
import { useLocationAccess } from './use-location-access';
import { isGeolocationSupported } from './geolocation';

/**
 * Shows the informational location-permission pop-up once after the user logs
 * in and lands in the authenticated part of the application.
 *
 * Behaviour:
 * - If the user has already been prompted before, nothing happens (we don't
 *   nag on every login).
 * - If the user previously enabled location access, we silently refresh their
 *   coordinates so document headers stay up to date.
 * - Otherwise we open the informational dialog requesting permission.
 *
 * This is intentionally best-effort and never blocks rendering, keeping the app
 * fully usable for users who never enable location services.
 */
export function useLocationPermissionPrompt() {
  const { openModal } = useModal();
  const { isLocationAccessEnabled, refreshCoordinates } = useLocationAccess();
  const [hasPrompted, setHasPrompted] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.LOCATION_PERMISSION_PROMPTED,
  );

  const hasRunRef = React.useRef(false);

  React.useEffect(() => {
    // Only ever run this effect once per mount of the authenticated app.
    if (hasRunRef.current) {
      return;
    }
    hasRunRef.current = true;

    if (!isGeolocationSupported()) {
      return;
    }

    // The user already opted in previously - keep their coordinates fresh.
    if (isLocationAccessEnabled) {
      refreshCoordinates().catch(() => {
        // refreshCoordinates never rejects (errors are handled internally),
        // this handler only keeps the promise from floating.
      });
      return;
    }

    // Already asked before and the user did not opt in - respect that choice.
    if (hasPrompted) {
      return;
    }

    setHasPrompted(true);
    openModal({
      modalType: 'componentModal',
      modalProps: {
        children: React.createElement(LocationPermissionDialog),
      },
    });
  }, [
    hasPrompted,
    isLocationAccessEnabled,
    openModal,
    refreshCoordinates,
    setHasPrompted,
  ]);
}

